import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback } from 'react';
import { ariaDescribedByIds, enumOptionsIndexForValue, enumOptionsValueForIndex } from '@rjsf/utils';

function getValue(event, multiple) {
  if (multiple) {
    return Array.from(event.target.options).slice().filter(function (o) {
      return o.selected;
    }).map(function (o) {
      return o.value;
    });
  }

  return event.target.value;
}
/** The `SelectWidget` is a widget for rendering dropdowns.
 *  It is typically used with string properties constrained with enum options.
 *
 * @param props - The `WidgetProps` for this component
 */


function SelectWidget(_ref) {
  var schema = _ref.schema,
      id = _ref.id,
      options = _ref.options,
      value = _ref.value,
      required = _ref.required,
      disabled = _ref.disabled,
      readonly = _ref.readonly,
      _ref$multiple = _ref.multiple,
      multiple = _ref$multiple === void 0 ? false : _ref$multiple,
      _ref$autofocus = _ref.autofocus,
      autofocus = _ref$autofocus === void 0 ? false : _ref$autofocus,
      onChange = _ref.onChange,
      onBlur = _ref.onBlur,
      onFocus = _ref.onFocus,
      placeholder = _ref.placeholder;
  var enumOptions = options.enumOptions,
      enumDisabled = options.enumDisabled,
      optEmptyVal = options.emptyValue;
  var emptyValue = multiple ? [] : '';
  var handleFocus = useCallback(function (event) {
    var newValue = getValue(event, multiple);
    return onFocus(id, enumOptionsValueForIndex(newValue, enumOptions, optEmptyVal));
  }, [onFocus, id, schema, multiple, enumOptions, optEmptyVal]);
  var handleBlur = useCallback(function (event) {
    var newValue = getValue(event, multiple);
    return onBlur(id, enumOptionsValueForIndex(newValue, enumOptions, optEmptyVal));
  }, [onBlur, id, schema, multiple, enumOptions, optEmptyVal]);
  var handleChange = useCallback(function (event) {
    var newValue = getValue(event, multiple);
    return onChange(enumOptionsValueForIndex(newValue, enumOptions, optEmptyVal));
  }, [onChange, schema, multiple, enumOptions, optEmptyVal]);
  var selectedIndexes = enumOptionsIndexForValue(value, enumOptions, multiple);
  var showPlaceholderOption = !multiple && schema.default === undefined;
  return _jsxs("select", {
    id: id,
    name: id,
    multiple: multiple,
    className: 'form-control',
    value: typeof selectedIndexes === 'undefined' ? emptyValue : selectedIndexes,
    required: required,
    disabled: disabled || readonly,
    autoFocus: autofocus,
    onBlur: handleBlur,
    onFocus: handleFocus,
    onChange: handleChange,
    "aria-describedby": ariaDescribedByIds(id),
    children: [showPlaceholderOption && _jsx("option", {
      value: '',
      children: placeholder
    }), Array.isArray(enumOptions) && enumOptions.map(function (_ref2, i) {
      var value = _ref2.value,
          label = _ref2.label;
      var disabled = enumDisabled && enumDisabled.indexOf(value) !== -1;
      return _jsx("option", {
        value: String(i),
        disabled: disabled,
        children: label
      }, i);
    })]
  });
}

export default SelectWidget;